import { useNavigate, useParams } from 'react-router'
import { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import './OrderDetails.css'
import { confidenceType, lineItemType, orderType, rejectionType, shipToType, statusDetailType } from '../types'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  ButtonGroup,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  ChevronLeft,
  DescriptionOutlined,
  EditOutlined,
  ExpandLess,
  ExpandMore,
  InfoOutline,
  IosShareOutlined,
  PauseCircleOutline,
  UpdateOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material'
import dayjs from 'dayjs'
import { buildStatusDetail, Expandable, formatAddress, getRepCodeName, isAddressEmpty } from '../helper'
import ErrorScreen from '../Misc/ErrorScreen'
import ViewDocuments from './ViewDocuments'
import UpdateStatus, { ScrmHold } from './UpdateStatus'
import { NetsuiteExport } from './NetsuiteExport'
import EditLines from '../EditLines/EditLines'
import ShippingData from './ShippingData'
import { useRepCodes, useUser } from '../hooks'
import EditOrder from './EditOrder'

function conf(code: number | null | undefined): string {
  if (code === -1) {
    return 'User entered'
  } else if (code === -2) {
    return 'Edited by OP'
  } else if (!code) {
    return 'AI did not read'
  } else {
    return `AI confidence: ${(code * 100).toFixed(0)}%`
  }
}

function OrderDetails() {
  const navigate = useNavigate()
  const user = useUser()
  const [repCodes, _setRepCodes] = useRepCodes()
  const { orderID } = useParams<{ orderID?: string }>()

  const [order, setOrder] = useState<orderType>({} as orderType)
  const [rejection, setRejection] = useState<rejectionType | null>(null)
  const [correction, setCorrection] = useState<rejectionType | null>(null)
  const [statusDetail, setStatusDetail] = useState<statusDetailType | null>(null)
  const [completed, setCompleted] = useState(order.Completed)
  const [confidence, setConfidence] = useState<confidenceType | null>(null)
  const [lines, setLineItems] = useState<lineItemType[]>([])
  const [cqBackup, setCqBackup] = useState(new Date())
  const [load, setLoad] = useState<'loading' | 'error' | 'unauthorized' | ''>('loading')
  const [viewDocumentsOpen, setViewDocumentsOpen] = useState(false)
  const [updateStatusOpen, setUpdateStatusOpen] = useState<boolean>(false)
  const [scrmHoldOpen, setScrmHoldOpen] = useState<boolean>(false)
  const [exportOpen, setExportOpen] = useState<boolean>(false)
  const [editOrderOpen, setEditOrderOpen] = useState<boolean>(false)
  const [editLinesOpen, setEditLinesOpen] = useState<boolean>(false)
  const [copyPopupOpen, setCopyPopupOpen] = useState<'quote' | 'salesOrder' | 'orderId' | 'opportunity' | null>(null)
  const [kitCollapsed, setKitCollapsed] = useState<string[]>([])
  const [showComments, setShowComments] = useState<{
    rejection: boolean
    cancelled: boolean
    holdOps: boolean
    holdSales: boolean
  }>({ rejection: false, cancelled: false, holdOps: false, holdSales: false })

  const LINE_ITEM_COLUMN_WIDTHS = {
    Number: lines.some(line => line.Bundle || line.Kit) ? '100px' : '60px',
    Quantity: '60px',
    Supplier: '10%',
    DisplayPartNumber: '10%',
    Description: undefined,
    Shipping: '15%',
    UnitCost: '5%',
    ExtCost: '5%',
    UnitPrice: '5%',
    ExtPrice: '5%',
    ProductCategory: '10%',
    Class: '10%',
  }

  const [shippingLineItem, setShippingLineItem] = useState<lineItemType | null>(null)
  const [lineItemsBulkExpand, setLineItemsBulkExpand] = useState<boolean>(false)

  const defaultShipTo = {
    ShipToAttention: order.ShipToAttention,
    ShipToCompany: order.ShipToCompany,
    ShipToAddress1: order.ShipToAddress1,
    ShipToAddress2: order.ShipToAddress2,
    ShipToAddressCity: order.ShipToAddressCity,
    ShipToAddressState: order.ShipToAddressState,
    ShipToAddressZip: order.ShipToAddressZip,
    ShipToAddressCountry: order.ShipToAddressCountry,
    ReceiverContactName: order.ReceiverContactName,
    ReceiverContactEmail: order.ReceiverContactEmail,
    ReceiverContactPhone: order.ReceiverContactPhone,
  }

  const InfoRow = ({ label, value, tooltip }: { label: string; value: string; tooltip?: string }) => {
    return (
      <div className="order-details-info-row">
        <Typography variant="caption" color="text.secondary">
          {label}:
        </Typography>
        <div style={{ width: load === 'loading' ? '40%' : 'auto' }}>
          {load === 'loading' ? (
            <Skeleton variant="text" width="100%" />
          ) : (
            <Typography variant="body2">{value}</Typography>
          )}
          {tooltip && (
            <Tooltip title={tooltip}>
              <InfoOutline color="action" />
            </Tooltip>
          )}
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (!user.email) {
      return
    }

    setLoad('loading')
    axios
      .get(`/order/${orderID}`)
      .then(response => {
        setOrder(response.data.order)
        setConfidence(response.data.confidence)
        setLineItems(response.data.lineItems)
        setCqBackup(response.data.cqBackup)
        setRejection(response.data.rejection)
        setCorrection(response.data.correction)
        setStatusDetail(
          buildStatusDetail(response.data.order.Status, response.data.order.StatusComment, response.data.rejection),
        )
        setCompleted(response.data.order.Completed)

        const repCode = repCodes.find(repCode => repCode.RepCode === response.data.order.RepCode)
        if (
          user.sales &&
          !user.admin &&
          !user.operations &&
          user.email.toLowerCase() !== response.data.order.CreatedBy.toLowerCase() &&
          ![
            repCode?.IsrEmail?.toLowerCase(),
            repCode?.AeEmail?.toLowerCase(),
            repCode?.EngEmail?.toLowerCase(),
          ].includes(user.email.toLowerCase()) &&
          user.email.toLowerCase() !== 'gclemens@imprestechnology.com'
        ) {
          setLoad('unauthorized')
        } else {
          setLoad('')
        }
        document.title = `Order | ${response.data.order.QuoteNumber ?? ''} | Order Portal`
      })
      .catch(err => {
        if (err.response) {
          // received error code other than 2xx
          console.error(err.response)
        } else if (err.request) {
          // no response received
          console.error(err.request)
        } else {
          // request was not made
          console.error(err.message)
        }
        setLoad('error')
      })
  }, [user])

  const flatLineItems = useMemo(() => {
    const withSortKey: { item: lineItemType; sortKey: number }[] = []
    for (const line of lines) {
      if (line.children?.length) {
        const minChildNum = Math.min(...line.children.map(c => c.Number ?? Infinity))
        withSortKey.push({ item: line, sortKey: minChildNum })
        for (const child of line.children) {
          withSortKey.push({ item: child, sortKey: child.Number ?? Infinity })
        }
      } else {
        withSortKey.push({ item: line, sortKey: line.Number ?? Infinity })
      }
    }
    withSortKey.sort((a, b) => a.sortKey - b.sortKey)
    return withSortKey.map(({ item }) => item)
  }, [lines])

  const shippingAddresses = useMemo(() => {
    const orderAddress = {
      ShipToAttention: order.ShipToAttention,
      ShipToCompany: order.ShipToCompany,
      ShipToAddress1: order.ShipToAddress1,
      ShipToAddress2: order.ShipToAddress2,
      ShipToAddressCity: order.ShipToAddressCity,
      ShipToAddressState: order.ShipToAddressState,
      ShipToAddressZip: order.ShipToAddressZip,
      ShipToAddressCountry: order.ShipToAddressCountry,
      ReceiverContactName: order.ReceiverContactName,
      ReceiverContactEmail: order.ReceiverContactEmail,
      ReceiverContactPhone: order.ReceiverContactPhone,
      default: true,
      id: 0,
    }
    const lineAddresses = flatLineItems.map(line => ({
      ShipToAttention: line.ShipToAttention,
      ShipToCompany: line.ShipToCompany,
      ShipToAddress1: line.ShipToAddress1,
      ShipToAddress2: line.ShipToAddress2,
      ShipToAddressCity: line.ShipToAddressCity,
      ShipToAddressState: line.ShipToAddressState,
      ShipToAddressZip: line.ShipToAddressZip,
      ShipToAddressCountry: line.ShipToAddressCountry,
      ReceiverContactName: line.ReceiverContactName,
      ReceiverContactEmail: line.ReceiverContactEmail,
      ReceiverContactPhone: line.ReceiverContactPhone,
      default: false,
      id: 0,
    }))
    const key = (a: shipToType) =>
      [
        a.ShipToAttention,
        a.ShipToCompany,
        a.ShipToAddress1,
        a.ShipToAddress2,
        a.ShipToAddressCity,
        a.ShipToAddressState,
        a.ShipToAddressZip,
        a.ShipToAddressCountry,
        a.ReceiverContactName,
        a.ReceiverContactEmail,
        a.ReceiverContactPhone,
      ].join('|')
    const seen = new Set<string>()
    const unique: (typeof orderAddress)[] = []
    for (const a of [orderAddress, ...lineAddresses]) {
      const k = key(a)
      if (seen.has(k)) continue
      seen.add(k)
      unique.push(a)
    }
    return unique
      .filter(address =>
        Object.entries(address).some(([key, value]) => value !== null && key !== 'default' && key !== 'id'),
      )
      .map((address, index) => ({ ...address, id: index }))
  }, [flatLineItems])

  return (
    <div className="order-details">
      {load === 'error' ? (
        <ErrorScreen
          title="Failed to load order"
          message="Failed to load order. Please try again or contact support if the problem persists."
        />
      ) : order.Status === 'Draft' ? (
        <ErrorScreen title="Order is a draft" message="This order is a draft and cannot be viewed." />
      ) : load === 'unauthorized' ? (
        <ErrorScreen title="Unauthorized" message="You are not authorized to view this order." />
      ) : (
        <>
          <div className="order-details-header">
            <div className="order-details-header-left">
              <Button variant="contained" startIcon={<ChevronLeft />} onClick={() => navigate('/order')}>
                Return to List
              </Button>
            </div>
            <div className="order-details-header-right">
              <Button
                variant="outlined"
                startIcon={<EditOutlined />}
                onClick={() => setEditOrderOpen(true)}
                style={{ display: !(user.admin || user.operations) ? 'none' : undefined }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                startIcon={<UpdateOutlined />}
                onClick={() => setUpdateStatusOpen(true)}
                disabled={completed}
                style={{ display: !(user.admin || user.operations) ? 'none' : undefined }}
              >
                Update Status
              </Button>
              <Button variant="outlined" startIcon={<DescriptionOutlined />} onClick={() => setViewDocumentsOpen(true)}>
                View Documents
              </Button>
              <Button
                variant="outlined"
                startIcon={<IosShareOutlined />}
                onClick={() => setExportOpen(true)}
                style={{ display: !(user.admin || user.operations) ? 'none' : undefined }}
              >
                Export to NetSuite
              </Button>
              <Button
                variant="outlined"
                startIcon={<PauseCircleOutline />}
                onClick={() => {
                  setScrmHoldOpen(true)
                }}
                style={{ display: !(user.admin || user.operations) ? 'none' : undefined }}
              >
                Set SCRM Hold
              </Button>
              <ViewDocuments orderId={orderID ?? ''} open={viewDocumentsOpen} setOpen={setViewDocumentsOpen} />
            </div>
            <UpdateStatus
              order={order}
              setOrder={setOrder}
              setRejection={setRejection}
              setStatusDetail={setStatusDetail}
              setCompleted={setCompleted}
              setOpen={setUpdateStatusOpen}
              open={updateStatusOpen}
            />
            <ScrmHold order={order} setOrder={setOrder} open={scrmHoldOpen} setOpen={setScrmHoldOpen} />
            <NetsuiteExport
              orderId={orderID ?? ''}
              salesOrderNumber={order.SalesOrderNumber}
              open={exportOpen}
              lineItems={flatLineItems}
              licenseEmail={order.LicenseContactEmail}
              licensePhone={order.LicenseContactPhone}
              setOpen={setExportOpen}
              setOrder={setOrder}
              quantities={flatLineItems.map(line => line.Quantity ?? 0)}
              enableLineItemShipping={shippingAddresses.length > 1}
            />
          </div>
          <div className="order-details-accordion-container">
            <Accordion defaultExpanded disableGutters>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Overview</Typography>
                {load === 'loading' ? (
                  <Skeleton variant="rounded" width="200px">
                    <Chip />
                  </Skeleton>
                ) : (
                  <Chip
                    label={`Quote #: ${order.QuoteNumber}`}
                    onClick={e => {
                      e.stopPropagation()
                      setCopyPopupOpen('quote')
                      navigator.clipboard.writeText(order.QuoteNumber ?? '')
                    }}
                  />
                )}
                {load === 'loading' ? (
                  <Skeleton variant="rounded" width="200px">
                    <Chip />
                  </Skeleton>
                ) : (
                  <Chip
                    label={`Sales Order #: ${order.SalesOrderNumber ?? '-'}`}
                    onClick={e => {
                      e.stopPropagation()
                      setCopyPopupOpen('salesOrder')
                      navigator.clipboard.writeText(order.SalesOrderNumber ?? '')
                    }}
                  />
                )}
                {load === 'loading' ? (
                  <Skeleton variant="rounded" width="200px">
                    <Chip />
                  </Skeleton>
                ) : (
                  <Chip
                    label={`Order ID: ${orderID}`}
                    onClick={e => {
                      e.stopPropagation()
                      setCopyPopupOpen('orderId')
                      navigator.clipboard.writeText(orderID ?? '')
                    }}
                  />
                )}
                {load === 'loading' ? (
                  <Skeleton variant="rounded" width="200px">
                    <Chip />
                  </Skeleton>
                ) : (
                  <Tooltip title={order.Opportunity}>
                    <Chip
                      label={`Salesforce Opportunity: ${
                        order.Opportunity
                          ? order.Opportunity.substring(0, 25) + (order.Opportunity.length > 25 ? '...' : '')
                          : '-'
                      }`}
                      onClick={e => {
                        e.stopPropagation()
                        setCopyPopupOpen('opportunity')
                        navigator.clipboard.writeText(order.Opportunity ?? '')
                      }}
                    />
                  </Tooltip>
                )}
                {order.Urgent && (
                  <Chip
                    label={`Urgent: ${order.UrgentReason ?? '-'}`}
                    color="warning"
                    variant="outlined"
                    icon={<WarningAmberOutlined />}
                  />
                )}
              </AccordionSummary>
              <Divider />
              <AccordionDetails>
                <div className="order-details-correction-rejection-container">
                  <div className="order-details-correction-rejection-item">
                    {(correction || rejection?.Correction) && (
                      <Chip label="Correction" color="warning" variant="outlined" size="small" />
                    )}
                    {rejection?.Correction && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Corrected Order:
                        </Typography>
                        {load === 'loading' ? (
                          <Skeleton variant="text" width="20%" />
                        ) : (
                          <Link href={`/order/${rejection.Correction}`}>{rejection.Correction}</Link>
                        )}
                      </>
                    )}
                    {correction && rejection && <Divider orientation="vertical" flexItem />}
                    {correction && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Original Order:
                        </Typography>
                        {/*link to the original order (what was corrected)*/}
                        {load === 'loading' ? (
                          <Skeleton variant="text" width="20%" />
                        ) : (
                          <Link href={`/order/${correction.Id}`}>{correction.Id}</Link>
                        )}
                      </>
                    )}
                  </div>
                  {statusDetail && (
                    <>
                      <div className="order-details-correction-rejection-item">
                        <Chip
                          label={statusDetail.label}
                          color={statusDetail.chipColor}
                          variant="outlined"
                          size="small"
                        />
                        {statusDetail.reason && (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              Reason:
                            </Typography>
                            {load === 'loading' ? (
                              <Skeleton variant="text" width="20%" />
                            ) : (
                              <Typography>{statusDetail.reason}</Typography>
                            )}
                          </>
                        )}
                        {statusDetail.comments && (
                          <IconButton
                            onClick={() =>
                              setShowComments({
                                ...showComments,
                                [statusDetail.commentsKey]: !showComments[statusDetail.commentsKey],
                              })
                            }
                          >
                            {showComments[statusDetail.commentsKey] ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                      </div>
                      {statusDetail.comments && (
                        <Collapse in={showComments[statusDetail.commentsKey]}>
                          <div className="order-details-correction-rejection-item">
                            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{statusDetail.comments}</Typography>
                          </div>
                        </Collapse>
                      )}
                      <Divider flexItem />
                    </>
                  )}
                </div>
                <Grid container spacing="56px">
                  <Grid size={12 / 5}>
                    <Stack spacing="4px">
                      <InfoRow label="Status" value={order.Status ?? '-'} />
                      <InfoRow
                        label="Timestamp"
                        value={
                          order.SubmissionTimestamp
                            ? dayjs(order.SubmissionTimestamp).format('MM/DD/YYYY hh:mm A')
                            : '-'
                        }
                      />
                      <InfoRow label="Submitted By" value={order.CreatedBy ?? '-'} />
                      <InfoRow label="Set Aside" value={order.SetAside ?? 'None'} />
                      <InfoRow label="Product Type" value={order.ProductType ?? '-'} />
                      <InfoRow
                        label="Award Date"
                        value={order.AwardDate ? dayjs.utc(order.AwardDate).format('MM/DD/YYYY') : '-'}
                        tooltip={conf(confidence?.AwardDate)}
                      />
                    </Stack>
                  </Grid>
                  <Grid size={12 / 5}>
                    <Stack spacing="4px">
                      <InfoRow label="Customer PO" value={order.CustomerPO ?? '-'} />
                      <InfoRow label="Gov. Award #" value={order.GovAwardNumber ?? '-'} />
                      <InfoRow label="DPAS Rating" value={order.DPASRating ?? '-'} />
                      <InfoRow
                        label="Delivery Due Date"
                        value={order.DeliveryDueDate ? dayjs.utc(order.DeliveryDueDate).format('MM/DD/YYYY') : '-'}
                        tooltip={conf(confidence?.DeliveryDueDate)}
                      />
                      <InfoRow label="Risk Buy" value={order.RiskBuy ? 'Yes' : 'No'} />
                      <InfoRow label="Urgent" value={order.Urgent ? order.UrgentReason ?? 'Yes' : 'No'} />
                    </Stack>
                  </Grid>
                  <Grid size={12 / 5}>
                    <Stack spacing="4px">
                      <InfoRow label="Credit Card Order" value={order.IsCreditCard ? 'Yes' : 'No'} />
                      <InfoRow
                        label="Customer Payment Terms"
                        value={order.GovPaymentTerms ?? '-'}
                        tooltip={conf(confidence?.GovPaymentTerms)}
                      />
                      <InfoRow label="Invoicing Platform" value={order.InvoicingPlatform ?? '-'} />
                      <InfoRow label="Partial Invoice?" value={order.PartialInvoicing ? 'Yes' : 'No'} />
                      <InfoRow label="Contains Service?" value={order.ContainsService ? 'Yes' : 'No'} />
                      <InfoRow label="Security Requirement" value={order.SecurityRequirement ?? '-'} />
                    </Stack>
                  </Grid>
                  <Grid size={12 / 5}>
                    <Stack spacing="4px">
                      <InfoRow
                        label="Rep Code"
                        value={
                          order.RepCode +
                          ' ' +
                          getRepCodeName(repCodes.find(repCode => repCode.RepCode === order.RepCode))
                        }
                      />
                      <InfoRow label="STN" value={order.STN ?? '-'} />
                      <InfoRow label="Purchase Vehicle" value={order.PurchaseVehicle ?? '-'} />
                      <InfoRow label="Contract #" value={order.ContractNumber ?? '-'} />
                      <InfoRow label="Division" value={order.Division ?? '-'} />
                      <InfoRow label="End User Type" value={order.EndUserType ?? '-'} />
                    </Stack>
                  </Grid>
                  <Grid size={12 / 5}>
                    <Stack spacing="4px">
                      <InfoRow
                        label="Order Total"
                        value={order.OrderTotal?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'}
                        tooltip={conf(confidence?.OrderTotal)}
                      />
                      <InfoRow
                        label="Total Cost"
                        value={order.TotalCost?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'}
                        tooltip={conf(confidence?.TotalCost)}
                      />
                      <InfoRow
                        label="Gross Margin"
                        value={
                          order.GrossMargin?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'
                        }
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded disableGutters>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Billing and Shipping Details / Miscellaneous</Typography>
              </AccordionSummary>
              <Divider />
              <AccordionDetails>
                <Grid container spacing="56px">
                  <Grid size={4} className="order-details-shipping-billing-container">
                    <div className="order-details-shipping-billing-header">
                      <Typography variant="h6">Billing</Typography>
                    </div>
                    <Stack spacing="4px">
                      <InfoRow label="Attention" value={order.BillToAttention ?? '-'} />
                      <InfoRow label="Addressee (Company)" value={order.BillToCompany ?? '-'} />
                      <InfoRow label="Address Line 1" value={order.BillToAddress1 ?? '-'} />
                      <InfoRow label="Address Line 2" value={order.BillToAddress2 ?? '-'} />
                      <InfoRow label="City" value={order.BillToAddressCity ?? '-'} />
                      <InfoRow label="State" value={order.BillToAddressState ?? '-'} />
                      <InfoRow label="Postal Code / Zip" value={order.BillToAddressZip ?? '-'} />
                      <InfoRow label="Country" value={order.BillToAddressCountry ?? '-'} />
                    </Stack>
                  </Grid>
                  <Grid size={8} className="order-details-shipping-billing-container">
                    <div className="order-details-shipping-billing-header">
                      <Typography variant="h6">Shipping</Typography>
                    </div>
                    <Grid container spacing="48px">
                      <Grid size={4}>
                        <Stack spacing="4px">
                          <InfoRow label="Attention" value={order.ShipToAttention ?? '-'} />
                          <InfoRow label="Addressee (Company)" value={order.ShipToCompany ?? '-'} />
                          <InfoRow label="Address Line 1" value={order.ShipToAddress1 ?? '-'} />
                          <InfoRow label="Address Line 2" value={order.ShipToAddress2 ?? '-'} />
                          <InfoRow label="City" value={order.ShipToAddressCity ?? '-'} />
                          <InfoRow label="State" value={order.ShipToAddressState ?? '-'} />
                          <InfoRow label="Postal Code / Zip" value={order.ShipToAddressZip ?? '-'} />
                          <InfoRow label="Country" value={order.ShipToAddressCountry ?? '-'} />
                        </Stack>
                      </Grid>
                      <Grid size={4}>
                        <Stack spacing="24px">
                          <Stack spacing="4px">
                            <InfoRow label="Receiver Contact Name" value={order.ReceiverContactName ?? '-'} />
                            <InfoRow label="Receiver Contact Email" value={order.ReceiverContactEmail ?? '-'} />
                            <InfoRow label="Receiver Contact Phone" value={order.ReceiverContactPhone ?? '-'} />
                          </Stack>
                          <Stack spacing="4px">
                            <InfoRow label="License Contact Name" value={order.LicenseContactName ?? '-'} />
                            <InfoRow label="License Contact Email" value={order.LicenseContactEmail ?? '-'} />
                            <InfoRow label="License Contact Phone" value={order.LicenseContactPhone ?? '-'} />
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid size={4}>
                        <Stack spacing="4px">
                          <InfoRow
                            label="Shipping Charges"
                            value={
                              order.ShippingCharges?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ??
                              '-'
                            }
                            tooltip={conf(confidence?.ShippingCharges)}
                          />
                          <InfoRow label="Delivery Instructions" value={order.DeliveryInstructions ?? '-'} />
                          <InfoRow label="Special Order/PO Instructions" value={order.SpecialInstructions ?? '-'} />
                        </Stack>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded disableGutters>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <div className="order-details-line-items-header">
                  <Typography variant="h6">Line Items</Typography>
                  <Button
                    variant="contained"
                    onClick={e => {
                      e.stopPropagation()
                      setEditLinesOpen(true)
                    }}
                  >
                    Edit Line Items
                  </Button>
                  <ButtonGroup variant="outlined">
                    <Button
                      onClick={e => {
                        e.stopPropagation()
                        setLineItemsBulkExpand(true)
                      }}
                    >
                      Expand all
                    </Button>
                    <Button
                      onClick={e => {
                        e.stopPropagation()
                        setLineItemsBulkExpand(false)
                      }}
                    >
                      Collapse all
                    </Button>
                  </ButtonGroup>
                  <Typography variant="body2" color="text.secondary">
                    Last CQ Backup: {new Date(cqBackup).toLocaleString()}
                  </Typography>
                </div>
              </AccordionSummary>
              <AccordionDetails className="order-details-line-items-container">
                <Table color="primary">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        align="center"
                        width={LINE_ITEM_COLUMN_WIDTHS.Number}
                        className="edit-lines-number-column"
                      >
                        #
                      </TableCell>
                      <TableCell width={LINE_ITEM_COLUMN_WIDTHS.Quantity}>Qty</TableCell>
                      <TableCell width={LINE_ITEM_COLUMN_WIDTHS.Supplier}>Supplier</TableCell>
                      <TableCell align="right" width={LINE_ITEM_COLUMN_WIDTHS.DisplayPartNumber}>
                        Display Part Number
                      </TableCell>
                      <TableCell width={LINE_ITEM_COLUMN_WIDTHS.Description}>Product Description</TableCell>
                      <TableCell width={LINE_ITEM_COLUMN_WIDTHS.Shipping}>Shipping Data</TableCell>
                      <TableCell align="right" width={LINE_ITEM_COLUMN_WIDTHS.UnitCost}>
                        Unit Cost
                      </TableCell>
                      <TableCell align="right" width={LINE_ITEM_COLUMN_WIDTHS.ExtCost}>
                        Ext Cost
                      </TableCell>
                      <TableCell align="right" width={LINE_ITEM_COLUMN_WIDTHS.UnitPrice}>
                        Unit Price
                      </TableCell>
                      <TableCell align="right" width={LINE_ITEM_COLUMN_WIDTHS.ExtPrice}>
                        Ext Price
                      </TableCell>
                      <TableCell width={LINE_ITEM_COLUMN_WIDTHS.ProductCategory}>Product Category</TableCell>
                      <TableCell width={LINE_ITEM_COLUMN_WIDTHS.Class}>Contract #</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {load === 'loading'
                      ? Array.from({ length: 3 }).map((_, index) => (
                          <TableRow key={index}>
                            {Array.from({ length: 12 }).map((_, index) => (
                              <TableCell key={index}>
                                <Skeleton variant="text" width="100%" />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      : flatLineItems.map((line, index) => (
                          <TableRow
                            key={index}
                            className={
                              line.Number === -1
                                ? 'kit-row'
                                : line.Number ===
                                  Math.max(
                                    ...flatLineItems
                                      .filter(item => item.Kit === line.Kit)
                                      .map(item => item.Number ?? 0),
                                  )
                                ? 'last-bundle-child'
                                : 'edit-lines-table-row'
                            }
                            sx={{
                              display:
                                line.Number !== -1 && kitCollapsed.includes(line.Kit ?? '') ? 'none' : 'table-row',
                            }}
                          >
                            {line.Number === -1 ? (
                              <TableCell align="center" className="edit-lines-number-column">
                                <Chip label={'Kit'} color="primary" variant="filled" size="small" />
                                <IconButton
                                  onClick={() =>
                                    setKitCollapsed(prev =>
                                      prev.includes(line.Kit ?? '')
                                        ? prev.filter(kit => kit !== line.Kit)
                                        : [...prev, line.Kit ?? ''],
                                    )
                                  }
                                >
                                  {kitCollapsed.includes(line.Kit ?? '') ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                              </TableCell>
                            ) : (
                              <TableCell align="center" className="edit-lines-number-column">
                                {line.Number}
                                {line.Bundle && <Chip label={'Bundle'} color="primary" variant="filled" size="small" />}
                              </TableCell>
                            )}
                            <TableCell>{line.Quantity}</TableCell>
                            <TableCell>{line.Supplier}</TableCell>
                            <TableCell align="right">{line.DisplayPartNumber ?? '-'}</TableCell>
                            <TableCell>
                              <Expandable
                                text={line.ProductDescription}
                                maxHeight={1}
                                expandButtonInline
                                bulkExpand={lineItemsBulkExpand}
                              />
                            </TableCell>
                            <TableCell className="order-details-shipping-data-column">
                              <Link
                                underline="none"
                                onClick={() => setShippingLineItem(line)}
                                className="order-details-shipping-data-link"
                              >
                                {isAddressEmpty(line) ? (
                                  <Expandable
                                    text={formatAddress(defaultShipTo)}
                                    maxHeight={1}
                                    expandButtonInline
                                    bulkExpand={lineItemsBulkExpand}
                                  />
                                ) : (
                                  <Expandable
                                    text={formatAddress(line)}
                                    maxHeight={1}
                                    expandButtonInline
                                    bulkExpand={lineItemsBulkExpand}
                                  />
                                )}
                              </Link>
                            </TableCell>
                            <TableCell align="right">
                              $
                              {line.UnitCost?.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell align="right">
                              $
                              {line.ExtCost?.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell align="right">
                              $
                              {line.UnitPrice?.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell align="right">
                              $
                              {line.ExtPrice?.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TableCell>
                            <TableCell>{line.ProductCategory ?? '-'}</TableCell>
                            <TableCell>{line.Class ?? '-'}</TableCell>
                          </TableRow>
                        ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          </div>
          <EditOrder
            originalOrder={order}
            setOriginalOrder={setOrder}
            onBack={() => setEditOrderOpen(false)}
            open={editOrderOpen}
            setOpen={setEditOrderOpen}
          />
          <EditLines
            flatLineItems={flatLineItems}
            setLineItems={setLineItems}
            quoteNumber={order.QuoteNumber ?? ''}
            shippingAddresses={shippingAddresses}
            setOpen={setEditLinesOpen}
            open={editLinesOpen}
            orderID={orderID ?? ''}
            user={user}
            originalFlatLineItems={flatLineItems}
            onReset={() => setLineItems(lines)}
            order={order}
            setOrder={setOrder}
            cqBackup={cqBackup}
          />
          <ShippingData
            lineItem={shippingLineItem ?? ({} as lineItemType)}
            defaultShipTo={defaultShipTo}
            open={shippingLineItem !== null}
            onClose={() => setShippingLineItem(null)}
            onSaved={updatedShipTo => {
              if (!shippingLineItem) return
              const { Id, Number: lineNum } = shippingLineItem
              const updateLine = (items: lineItemType[]): lineItemType[] =>
                items.map(line =>
                  line.Id === Id && line.Number === lineNum
                    ? { ...line, ...updatedShipTo }
                    : line.children
                    ? { ...line, children: updateLine(line.children) }
                    : line,
                )
              setLineItems(prev => updateLine(prev))
            }}
          />
        </>
      )}
      <Snackbar
        open={!!copyPopupOpen}
        autoHideDuration={5000}
        onClose={() => setCopyPopupOpen(null)}
        message={
          copyPopupOpen === 'quote'
            ? 'Quote number copied to clipboard'
            : copyPopupOpen === 'salesOrder'
            ? 'Sales order number copied to clipboard'
            : copyPopupOpen === 'orderId'
            ? 'Order ID copied to clipboard'
            : 'Opportunity copied to clipboard'
        }
      />
    </div>
  )
}

export default OrderDetails
